import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../services/api";
import { useNotification } from "../../../context/NotificationContext";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaBoxOpen,
  FaCloudUploadAlt,
  FaTimes,
  FaCheck,
} from "react-icons/fa";

const isActive = (value) => value === true || value === 1 || value === "1";

function getImageUrl(image) {
  if (typeof image !== "string" || !image.trim()) return "";

  const value = image.trim().replace(/\\/g, "/");

  if (/^https?:\/\//i.test(value)) return value;

  // הנתיב שבו מוצגות תמונות המוצרים בשרת המקומי.
  const baseURL = api.defaults.baseURL || window.location.origin;
  const serverOrigin = new URL(baseURL, window.location.origin).origin;

  if (value.startsWith("/uploads/")) {
    return `${serverOrigin}${value}`;
  }

  if (value.startsWith("uploads/")) {
    return `${serverOrigin}/${value}`;
  }

  return `${serverOrigin}/uploads/products/${encodeURIComponent(value)}`;
}

function ProductImage({ image, alt, className }) {
  const [failed, setFailed] = useState(false);
  const src = getImageUrl(image);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <span
        className="product-thumb-placeholder"
        role="img"
        aria-label={src ? "לא ניתן להציג את התמונה" : "אין תמונה"}
      >
        <FaBoxOpen />
      </span>
    );
  }

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
}

function ProductsAdmin() {
  const { showNotification, confirmAction } = useNotification();

  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadLock = useRef(false);
  const saveLock = useRef(false);
  const busy = uploading || saving;

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchProducts = async () => {
    const res = await api.get("/products", {
      headers: getAuthHeaders(),
    });

    if (!Array.isArray(res.data)) {
      throw new Error("Invalid products response");
    }

    setProducts(res.data);
    setLoadError("");
  };

  useEffect(() => {
    let active = true;

    api
      .get("/products", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((res) => {
        if (!Array.isArray(res.data)) {
          throw new Error("Invalid products response");
        }

        if (active) setProducts(res.data);
      })
      .catch(() => {
        if (active) {
          setLoadError("טעינת המוצרים נכשלה. נסו לרענן את העמוד.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return products;

    return products.filter((product) =>
      [product.name, product.description, product.product_id].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [products, search]);

  const updateField = (name, value) => {
    setEditingProduct((previous) =>
      previous ? { ...previous, [name]: value } : previous,
    );
  };

  const closeEditor = () => {
    if (uploadLock.current || saveLock.current) return;
    setEditingProduct(null);
  };

  const openNewProduct = () => {
    if (uploadLock.current || saveLock.current) return;

    setEditingProduct({
      name: "",
      description: "",
      price: "",
      stock: "",
      weight: "",
      warranty: "",
      image: "",
      is_active: true,
    });
  };

  const openEditProduct = (product) => {
    if (uploadLock.current || saveLock.current) return;

    setEditingProduct({
      ...product,
      is_active: isActive(product.is_active),
    });
  };

  const saveProduct = async (event) => {
    event.preventDefault();

    if (!editingProduct || uploadLock.current || saveLock.current) {
      return;
    }

    const name = String(editingProduct.name || "").trim();
    const price = Number(editingProduct.price);
    const stock = Number(editingProduct.stock);
    const weight =
      editingProduct.weight === "" || editingProduct.weight == null
        ? null
        : Number(editingProduct.weight);

    if (!name) {
      showNotification("יש להזין שם מוצר", "warning");
      return;
    }

    if (
      String(editingProduct.price ?? "").trim() === "" ||
      !Number.isFinite(price) ||
      price < 0
    ) {
      showNotification("יש להזין מחיר תקין", "warning");
      return;
    }

    if (
      String(editingProduct.stock ?? "").trim() === "" ||
      !Number.isInteger(stock) ||
      stock < 0
    ) {
      showNotification("יש להזין כמות מלאי שלמה ולא שלילית", "warning");
      return;
    }

    if (weight !== null && (!Number.isFinite(weight) || weight < 0)) {
      showNotification("יש להזין משקל תקין", "warning");
      return;
    }

    const payload = {
      name,
      description: editingProduct.description || "",
      price,
      stock,
      weight,
      warranty: editingProduct.warranty || "",
      image: editingProduct.image || "",
      is_active: isActive(editingProduct.is_active),
    };

    saveLock.current = true;
    setSaving(true);

    try {
      if (editingProduct.product_id) {
        await api.put(`/products/${editingProduct.product_id}`, payload, {
          headers: getAuthHeaders(),
        });

        showNotification("המוצר עודכן בהצלחה", "success");
      } else {
        await api.post("/products", payload, {
          headers: getAuthHeaders(),
        });

        showNotification("המוצר נוסף בהצלחה", "success");
      }

      setEditingProduct(null);

      try {
        await fetchProducts();
      } catch {
        showNotification(
          "המוצר נשמר, אך רענון הרשימה נכשל. רעננו את העמוד.",
          "warning",
        );
      }
    } catch (error) {
      const status = error.response?.status;

      showNotification(
        status
          ? `שמירת המוצר נכשלה — קוד שגיאה ${status}`
          : "לא ניתן להתחבר לשרת",
        "error",
      );
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    const confirmed = await confirmAction({
      title: "מחיקת מוצר",
      message: "האם להסיר את המוצר מהקטלוג הפעיל?",
      confirmText: "מחיקה",
    });

    if (!confirmed) return;

    try {
      await api.delete(`/products/${id}`, {
        headers: getAuthHeaders(),
      });
    } catch {
      showNotification("מחיקת המוצר נכשלה", "error");
      return;
    }

    showNotification("המוצר הוסר מהקטלוג הפעיל", "success");

    try {
      await fetchProducts();
    } catch {
      showNotification("המוצר הוסר, אך רענון הרשימה נכשל.", "warning");
    }
  };

  const uploadProductImage = async (file) => {
    if (!file || !editingProduct || uploadLock.current || saveLock.current) {
      return;
    }

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      showNotification("יש לבחור תמונה מסוג JPG או PNG", "warning");
      return;
    }

    uploadLock.current = true;
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      // הדפדפן מגדיר את כותרת multipart ואת גבולות הבקשה.
      const res = await api.post("/upload/product", formData, {
        headers: getAuthHeaders(),
      });

      const filename = res.data?.filename;

      if (typeof filename !== "string" || !filename.trim()) {
        showNotification("השרת לא החזיר שם קובץ תקין לתמונה", "error");
        return;
      }

      setEditingProduct((previous) =>
        previous ? { ...previous, image: filename.trim() } : previous,
      );

      showNotification(
        "התמונה הועלתה. לחצו על שמירת מוצר לשמירת השינוי.",
        "success",
      );
    } catch (error) {
      const status = error.response?.status;

      showNotification(
        status
          ? `העלאת התמונה נכשלה — קוד שגיאה ${status}`
          : "לא ניתן להתחבר לשרת לצורך העלאת התמונה",
        "error",
      );
    } finally {
      uploadLock.current = false;
      setUploading(false);
    }
  };

  const getStockStatus = (stock) => {
    const amount = Number(stock);

    if (amount <= 0) {
      return {
        label: "אזל מהמלאי",
        className: "out-stock-badge",
      };
    }

    if (amount <= 5) {
      return {
        label: "מלאי נמוך",
        className: "low-stock-badge",
      };
    }

    return {
      label: "במלאי",
      className: "stock-badge",
    };
  };

  const activeCount = products.filter((product) =>
    isActive(product.is_active),
  ).length;

  const lowStockCount = products.filter(
    (product) => Number(product.stock) <= 5,
  ).length;

  return (
    <section className="dashboard-section products-section" dir="rtl" lang="he">
      <div className="section-title">
        <div className="section-title-main">
          <span className="section-icon">
            <FaBoxOpen />
          </span>

          <div>
            <h2>ניהול מוצרים</h2>
            <p>קטלוג, מלאי, מחירים ותמונות מוצרים</p>
          </div>
        </div>

        <button
          type="button"
          className="add-product-btn"
          onClick={openNewProduct}
          disabled={busy}
        >
          <FaPlus />
          הוספת מוצר
        </button>
      </div>

      <div className="admin-mini-stats">
        <div>
          <strong>{products.length}</strong>
          <span>מוצרים ברשימה</span>
        </div>

        <div>
          <strong>{activeCount}</strong>
          <span>מוצרים פעילים</span>
        </div>

        <div className={lowStockCount ? "warning" : ""}>
          <strong>{lowStockCount}</strong>
          <span>דורשים בדיקת מלאי</span>
        </div>
      </div>

      <div className="entity-toolbar">
        <div className="orders-search compact-search">
          <FaSearch />

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="חיפוש מוצר לפי שם או מספר..."
            aria-label="חיפוש מוצרים"
          />
        </div>

        <span className="section-count">{filteredProducts.length} מוצרים</span>
      </div>

      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr>
              <th>מוצר</th>
              <th>מחיר</th>
              <th>מלאי</th>
              <th>מצב מלאי</th>
              <th>אחריות</th>
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>

          <tbody>
            {loading || loadError || filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-table-cell">
                  {loading ? "טוען מוצרים..." : loadError || "אין מוצרים להצגה"}
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => {
                const stockStatus = getStockStatus(product.stock);
                const active = isActive(product.is_active);

                return (
                  <tr
                    key={product.product_id}
                    className={
                      Number(product.stock) <= 0
                        ? "product-row-out"
                        : Number(product.stock) <= 5
                          ? "product-row-low"
                          : ""
                    }
                  >
                    <td>
                      <div className="product-name-cell">
                        <ProductImage
                          image={product.image}
                          alt={product.name}
                          className="product-table-thumb"
                        />

                        <div>
                          <strong>{product.name}</strong>
                          <small>מוצר #{product.product_id}</small>
                        </div>
                      </div>
                    </td>

                    <td>
                      <strong>
                        {Number(product.price).toLocaleString("he-IL", {
                          style: "currency",
                          currency: "ILS",
                        })}
                      </strong>
                    </td>

                    <td>
                      <strong>{product.stock}</strong>
                    </td>

                    <td>
                      <span className={stockStatus.className}>
                        {stockStatus.label}
                      </span>
                    </td>

                    <td>{product.warranty || "-"}</td>

                    <td>
                      <span
                        className={
                          active
                            ? "product-active-badge"
                            : "product-inactive-badge"
                        }
                      >
                        {active ? "פעיל" : "לא פעיל"}
                      </span>
                    </td>

                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="table-btn"
                          disabled={busy}
                          onClick={() => openEditProduct(product)}
                          aria-label={`עריכת ${product.name}`}
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          className="table-btn danger"
                          disabled={busy}
                          onClick={() => deleteProduct(product.product_id)}
                          aria-label={`מחיקת ${product.name}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editingProduct && (
        <div
          className="product-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-editor-title"
          onKeyDown={(event) => {
            if (event.key === "Escape") closeEditor();
          }}
        >
          <form
            className="product-modal-card"
            onSubmit={saveProduct}
            aria-busy={busy}
          >
            <div className="form-modal-header">
              <div>
                <span className="modal-eyebrow">עריכת פרטי מוצר</span>

                <h2 id="product-editor-title">
                  {editingProduct.product_id ? "עריכת מוצר" : "הוספת מוצר חדש"}
                </h2>

                <p>מלאו את פרטי המוצר ושמרו את השינויים.</p>
              </div>

              <button
                type="button"
                className="modal-close-btn"
                onClick={closeEditor}
                disabled={busy}
                aria-label="סגירה"
              >
                <FaTimes />
              </button>
            </div>

            <div className="product-form-grid">
              <label className="form-field full">
                <span>שם המוצר</span>
                <input
                  autoFocus
                  required
                  disabled={busy}
                  placeholder="לדוגמה: כרית Cervica"
                  value={editingProduct.name ?? ""}
                  onChange={(event) => updateField("name", event.target.value)}
                />
              </label>

              <label className="form-field full">
                <span>תיאור המוצר</span>
                <textarea
                  disabled={busy}
                  placeholder="תיאור קצר וברור של המוצר..."
                  value={editingProduct.description ?? ""}
                  onChange={(event) =>
                    updateField("description", event.target.value)
                  }
                />
              </label>

              <label className="form-field">
                <span>מחיר (₪)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  disabled={busy}
                  placeholder="0"
                  value={editingProduct.price ?? ""}
                  onChange={(event) => updateField("price", event.target.value)}
                />
              </label>

              <label className="form-field">
                <span>כמות במלאי</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  required
                  disabled={busy}
                  placeholder="0"
                  value={editingProduct.stock ?? ""}
                  onChange={(event) => updateField("stock", event.target.value)}
                />
              </label>

              <label className="form-field">
                <span>משקל</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  disabled={busy}
                  placeholder="0"
                  value={editingProduct.weight ?? ""}
                  onChange={(event) =>
                    updateField("weight", event.target.value)
                  }
                />
              </label>

              <label className="form-field">
                <span>אחריות</span>
                <input
                  disabled={busy}
                  placeholder="לדוגמה: שנה"
                  value={editingProduct.warranty ?? ""}
                  onChange={(event) =>
                    updateField("warranty", event.target.value)
                  }
                />
              </label>
            </div>

            <div className="product-media-panel">
              <label className="upload-box">
                <span className="upload-icon">
                  <FaCloudUploadAlt />
                </span>

                <strong>
                  {uploading ? "מעלה תמונה..." : "העלאת תמונת מוצר"}
                </strong>

                <small>לחצו לבחירת JPG / PNG</small>

                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  disabled={busy}
                  aria-label="בחירת תמונת מוצר"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    uploadProductImage(file);
                  }}
                />
              </label>

              {editingProduct.image ? (
                <div className="product-preview-wrap">
                  <ProductImage
                    image={editingProduct.image}
                    alt="תצוגה מקדימה של המוצר"
                    className="product-preview"
                  />
                  <span>תצוגה מקדימה</span>
                </div>
              ) : (
                <div className="product-preview-empty">
                  <FaBoxOpen />
                  <span>אין תמונה עדיין</span>
                </div>
              )}
            </div>

            {uploading && (
              <p role="status">התמונה בהעלאה. המתינו לסיום לפני שמירת המוצר.</p>
            )}

            <label className="active-toggle-row">
              <input
                type="checkbox"
                disabled={busy}
                checked={isActive(editingProduct.is_active)}
                onChange={(event) =>
                  updateField("is_active", event.target.checked)
                }
              />

              <span className="toggle-ui">
                <span />
              </span>

              <div>
                <strong>המוצר פעיל באתר</strong>
                <small>כיבוי האפשרות יסתיר את המוצר מהלקוחות.</small>
              </div>
            </label>

            <div className="modal-actions">
              <button type="submit" className="primary" disabled={busy}>
                <FaCheck />
                {uploading
                  ? "מעלה תמונה..."
                  : saving
                    ? "שומר..."
                    : "שמירת מוצר"}
              </button>

              <button
                type="button"
                className="secondary"
                onClick={closeEditor}
                disabled={busy}
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default ProductsAdmin;
